import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react'
import { layerStore } from '@shared/openlayers/tools/volume/utils/store/layer-store'
import { measurementStore } from '@shared/openlayers/tools/volume/utils/store/measurement-store'
import { MeasurementContainer } from './measurement/measurement'
import { ColorLegend } from './chartjs/color-legend'
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { VolumeThreejs } from './viz-3d/volume-threejs'
import { CalcSurface } from '../../../../../typings/reality-capture/volumes/models';
import { SelectSurface, ButtonSelectSurface } from './property-selectors/surface-selection';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { Toggle } from '@shared/ui-react/Toggle';
import { Button } from '@shared/ui-react/Button';
import { VolumeCrossSection } from './viz-3d/volume-cross-section';
import { AlignmentSelect } from './property-selectors/alignment-select';

// 3D view vertical scale bounds (must match the scale slider's min/max). Whole numbers only.
const SCALE_MIN = 1;
const SCALE_MAX = 20;
const DEFAULT_SCALE = 10;
// Auto-fit targets the scaled height at 1/this of the longer footprint dimension.
const HEIGHT_TO_FOOTPRINT_DIVISOR = 5;

type SurfaceBounds = { x_min: number; x_max: number; y_min: number; y_max: number; z_min: number; z_max: number };

const computeAutoScale = (bounds?: SurfaceBounds): number => {
    if (!bounds) return DEFAULT_SCALE;
    const width = bounds.x_max - bounds.x_min;
    const depth = bounds.y_max - bounds.y_min;
    const height = bounds.z_max - bounds.z_min;
    if (height <= 0) return DEFAULT_SCALE;
    // Scaled height ≈ 1/5 of the longer footprint dimension: 3D but not too tall.
    // Flat terrain → large scale; tall/small-footprint areas → smaller scale.
    const raw = Math.max(width, depth) / (height * HEIGHT_TO_FOOTPRINT_DIVISOR);
    const rounded = Math.round(raw);
    return Math.min(SCALE_MAX, Math.max(SCALE_MIN, rounded));
};

export const VolumeCalculationConfig = observer(({ toolsStore }: { toolsStore: MapToolsStore }) => {
    const VolumeToolSync = toolsStore.volumeTool.VisibleMeasurementsSync
    const { target, reference, drawCount } = layerStore
    const { measurements, selectedMeasurementUuid } = measurementStore
    const selectedMeasurement = measurementStore.getSelectedMeasurement()
    const [ isSelectionOpen, setIsSelectionOpen ] = useState(true)
    const [ bottomView, setBottomView ] = useState<'3d' | 'cross-section'>('3d')
    const [ lonOffset, setLonOffset ] = useState(0)
    const [ csStore, setCsStore ] = useState<any>(null)
    const [ scale, setScale ] = useState(10)
    const [ volumeToolType, setVolumeToolType ] = useState({
        name: 'stockpile',
        index: 0
    })

    // Fit the 3D vertical scale to each measurement's terrain. Keyed on the bounds
    // values (not object identity) so it recomputes only when the surface changes,
    // leaving the user's manual slider adjustments intact in between.
    const vizBounds = selectedMeasurement?.viz_surface?.bounds as SurfaceBounds | undefined;
    const vizBoundsKey = vizBounds
        ? `${vizBounds.x_min},${vizBounds.x_max},${vizBounds.y_min},${vizBounds.y_max},${vizBounds.z_min},${vizBounds.z_max}`
        : null;
    useEffect(() => {
        if (!vizBoundsKey) return;
        setScale(computeAutoScale(vizBounds));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vizBoundsKey])

    const selectReference = (reference: CalcSurface) => {
        layerStore.setReference(reference)
    };

    const selectTarget = (target: CalcSurface) => {
        layerStore.setTarget(target)
    };

    const clearReference = () => {
        layerStore.setReference(null)
    };

    const clearTarget = () => {
        layerStore.setTarget(null)
    };

    const changeVolumeToolType = (index: number) => {
        if (index === 0) setVolumeToolType({ name: 'stockpile', index });
        else if (index === 1) setVolumeToolType({ name: 'comparison', index });
        else if (index === 2) setVolumeToolType({ name: 'alignment', index });
        clearTarget()
        clearReference()
    };

    useEffect(() => {
        if (drawCount === 0) return;
        const canCalculate =
            (volumeToolType.name === 'stockpile' && !!reference) ||
            ((volumeToolType.name === 'comparison' || volumeToolType.name === 'alignment') && !!reference && !!target);
        if (canCalculate) {
            toolsStore.volumeTool.calculatePendingVolume();
        }
    }, [drawCount, reference, target, volumeToolType.name]);

    return (
        <div className="flex flex-col h-screen">
            <VolumeToolSync />

            {/* Tab panel */}
            <div className="pt-3 bg-ik-surface-dim border-b border-ik-border-weak">
                <Tabs index={volumeToolType.index} onChange={changeVolumeToolType} hideSingleTab={false} displayValue="block">
                    <TabPane title={__('js_map_measuring_tools.stockpile')}></TabPane>
                    <TabPane title={__('js_map_measuring_tools.comparison')}></TabPane>
                    <TabPane title={__('js_map_measuring_tools.alignment')}></TabPane>
                </Tabs>
            </div>

            <div className="pt-2 px-5">
                <div className="relative">
                    {/* Left content scrolls (capped) so the viz keeps space on short screens.
                        Margin (not padding) reserves the legend's space so the scrollbar sits
                        beside the legend, and the legend — pinned in the relative parent — never
                        scrolls or overflows. */}
                    <div className={`min-h-0 max-h-[45vh] overflow-y-auto ${selectedMeasurementUuid !== null ? 'mr-[166px]' : ''}`}>
                        {/* Surface Select */}
                        {isSelectionOpen && (
                            <div>
                                {/* Stockpile */}
                                {volumeToolType.name === 'stockpile' && (
                                    <div className="mb-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-gray-800 tracking-tight text-sm">
                                                1. {__('js_map_measuring_tools.select_dem')}
                                            </span>
                                        </div>
                                        <SelectSurface
                                            toolsStore={toolsStore}
                                            selection={reference}
                                            onSelect={selectReference}
                                            onClear={clearReference}
                                            disableOptions={true}
                                        />
                                    </div>
                                )}

                                {/* Comparison */}
                                {volumeToolType.name === 'comparison' && (
                                    <div className="mb-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-gray-800 tracking-tight text-sm">1. {__('js_map_measuring_tools.from_surface')}</span>
                                        </div>
                                        <ButtonSelectSurface
                                            toolsStore={toolsStore}
                                            selection={reference}
                                            onSelect={selectReference}
                                            onClear={clearReference}
                                        />
                                        <div className="flex items-center justify-between pt-4 mb-2">
                                            <span className="font-semibold text-gray-800 tracking-tight text-sm">2. {__('js_map_measuring_tools.to_surface')}</span>
                                        </div>
                                        <ButtonSelectSurface
                                            toolsStore={toolsStore}
                                            selection={target}
                                            onSelect={selectTarget}
                                            onClear={clearTarget}
                                        />
                                    </div>
                                )}

                                {/* Alignment */}
                                {volumeToolType.name === 'alignment' && (
                                    <AlignmentSelect
                                        toolsStore={toolsStore}
                                        reference={reference}
                                        target={target}
                                        selectReference={selectReference}
                                        selectTarget={selectTarget}
                                        clearReference={clearReference}
                                        clearTarget={clearTarget}
                                    />
                                )}
                            </div>
                        )}

                        {/* Show measurement */}
                        <MeasurementContainer
                            measurements={measurements}
                            measurement={selectedMeasurement}
                            measurementToolIndex={volumeToolType.index}
                            toolsStore={toolsStore}
                            isSelectionOpen={isSelectionOpen}
                            onToggleSelection={() => setIsSelectionOpen(!isSelectionOpen)}
                        />
                    </div>

                    {/* Color Legend — absolutely positioned so it never drives the container height */}
                    {selectedMeasurementUuid !== null && (
                        <div className="absolute top-0 right-0 bottom-0 w-[150px]">
                            <ColorLegend measurement={selectedMeasurement} />
                        </div>
                    )}
                </div>
            </div>

            {/* Viz mode */}
            <div className={`flex-1 min-h-0 overflow-hidden mt-2.5 flex flex-col ${selectedMeasurementUuid !== null ? 'bg-ik-surface-dim' : 'bg-white'}`}>
                {selectedMeasurementUuid !== null && (
                    <div className="border-t border-ik-border-weak shrink-0" />
                )}
                {selectedMeasurementUuid !== null && (
                    <div className="relative flex-1 min-h-0">
                        {/* Toggle overlaid on canvas */}
                        <div className="absolute top-2 left-4 z-[10000] flex items-center gap-2 bg-white/85 rounded-md px-3 py-1 shadow-sm">
                            <span className="text-sm font-medium">{__('js_map_measuring_tools.view_3d')}</span>
                            <Toggle
                                checked={bottomView === 'cross-section'}
                                onClick={() => { setBottomView(prev => prev === '3d' ? 'cross-section' : '3d'); setLonOffset(0); setCsStore(null); }}
                            />
                            <span className="text-sm font-medium">{__('js_map_measuring_tools.cross_section')}</span>
                            {bottomView === '3d' && (
                                <>
                                    <div className="w-px h-4 bg-ik-border-default mx-1" />
                                    <span className="text-sm font-medium">{__('js_map_measuring_tools.scale')}</span>
                                    <input
                                        type="range"
                                        min={1} max={20} step={1}
                                        value={scale}
                                        onChange={e => setScale(Number(e.target.value))}
                                        className="w-24 h-1 accent-ik-accent-primary cursor-pointer"
                                    />
                                    <input
                                        type="number"
                                        min={1} max={20} step={1}
                                        value={scale}
                                        onChange={e => setScale(Number(e.target.value))}
                                        className="w-12 text-sm border border-ik-border-default rounded px-1 py-0.5 bg-transparent"
                                    />
                                </>
                            )}
                            {bottomView === 'cross-section' && (
                                <>
                                    <div className="w-px h-4 bg-ik-border-default mx-1" />
                                    <Button secondary small icon="iki iki-chevron-left" onClick={() => setLonOffset(o => o - 0.0001)} tooltip="Move west" />
                                    <Button secondary small icon="iki iki-chevron-right" onClick={() => setLonOffset(o => o + 0.0001)} tooltip="Move east" />
                                </>
                            )}
                            {bottomView === 'cross-section' && csStore?.csCanvas && (
                                <>
                                    <div className="w-px h-4 bg-ik-border-default mx-1" />
                                    <Button
                                        secondary small
                                        icon="iki iki-cursor"
                                        tooltip={__('js_map_measuring_tools.clear_and_disable')}
                                        className={!csStore.csCanvas.distanceToolActive && !csStore.csCanvas.areaToolActive ? 'active' : null}
                                        onClick={() => { csStore.csCanvas.clearAreaTool(); csStore.csCanvas.clearDistanceTool(); }}
                                    />
                                    <Button
                                        secondary small
                                        icon="iki iki-measure"
                                        tooltip={__('js_map_measuring_tools.distance')}
                                        className={csStore.csCanvas.distanceToolActive ? 'active' : null}
                                        onClick={() => csStore.csCanvas.activateDistanceTool()}
                                    />
                                    <Button
                                        secondary small
                                        icon="iki iki-area"
                                        tooltip={__('js_map_measuring_tools.area')}
                                        className={csStore.csCanvas.areaToolActive ? 'active' : null}
                                        onClick={() => csStore.csCanvas.activateAreaTool()}
                                    />
                                    <Button secondary small onClick={csStore.clearAllTools}>
                                        {__('cross_section.clear_tools')}
                                    </Button>
                                </>
                            )}
                        </div>
                        {bottomView === '3d' && (
                            <VolumeThreejs measurement={selectedMeasurement} scale={scale} />
                        )}
                        {bottomView === 'cross-section' && (
                            <VolumeCrossSection measurement={selectedMeasurement} toolsStore={toolsStore} lonOffset={lonOffset} onStoreReady={setCsStore} />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
})

